import { z } from "zod";
import {
    BusinessTaxReturns,
    EntityProfileIndustry,
    EntityProfileRevenueBracket,
    EntityProfileType,
    FirmSize,
} from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { BillingDtoSchema } from "./billing.schema";

export enum OrganizationStageOfBusiness {
    EARLY = "early",
    ESTABLISHED = "established",
    GROWING = "growing",
    VENTURE_BACKED = "venture_backed",
}

export enum ProjectTimeline {
    ONE_TIME = "one_time",
    ONGOING_SUPPORT = "ongoing_support",
    UNSURE = "unsure",
}

export enum BudgetType {
    LESS_THAN_500 = "<$500",
    BETWEEN_500_AND_1000 = "$500 - $1000",
    BETWEEN_1000_AND_2000 = "$1000 - $2000",
    MORE_THAN_2000 = "$2000+",
}

export enum MeetingOption {
    IN_PERSON = "in_person",
    VIRTUAL = "virtual",
    HYBRID = "hybrid",
    NO_PREFERENCE = "no_preference",
}

export enum OrganizationBillingTransferMode {
    REQUEST = "request",
    INSTANT = "instant",
}

export const OrganizationProfileSchema = z.object({
    firmSize: z.nativeEnum(FirmSize).catch(FirmSize["1_19"]),
    businessTaxReturns: z.nativeEnum(BusinessTaxReturns).optional(),
    servicesOffered: z.array(z.nativeEnum(OrganizationServicesOffered)),

    accountantDirectoryEligible: z.boolean().optional(),
    // Matching
    supportedEntityTypes: z
        .array(z.nativeEnum(EntityProfileType))
        .max(50)
        .optional(),
    multiEntitySupport: z.boolean().optional(),
    stagesOfBusiness: z
        .array(z.nativeEnum(OrganizationStageOfBusiness))
        .max(50)
        .optional(),
    revenueBrackets: z
        .array(z.nativeEnum(EntityProfileRevenueBracket))
        .max(50)
        .optional(),
    industries: z.array(z.nativeEnum(EntityProfileIndustry)).max(50).optional(),
    locations: z.array(z.string().max(200)).max(50).optional(),
    projectTimelines: z.array(z.nativeEnum(ProjectTimeline)).max(50).optional(),
    budgets: z.array(z.nativeEnum(BudgetType)).max(50).optional(),
    meetingOptions: z.array(z.nativeEnum(MeetingOption)).max(50).optional(),

    // Selling
    calendlyUrl: z.string().url().optional(),
    websiteUrl: z.string().url().optional(),
    writeup: z.string().max(5000).optional(),
    videoUrl: z.string().url().optional(),
    googleReviewsUrl: z.string().url().optional(),
    samslistUrl: z.string().url().optional(),
    facebookUrl: z.string().url().optional(),
    yelpUrl: z.string().url().optional(),
    linkedinUrl: z.string().url().optional(),
    twitterUrl: z.string().url().optional(),
    youtubeUrl: z.string().url().optional(),
    idealClientDescription: z.string().max(5000).optional(),
});

export const OrganizationUserAssignmentSchema = z.object({
    organizationId: z.string(),
    userId: z.number(),
    role: z.nativeEnum(OrganizationUserRole),
    createdAt: z.coerce.date(),
    assignToNewClients: z.boolean(),
});

export const OrganizationDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable(),
    onboardingCompletedAt: z.coerce.date().nullable(),
    platformEnabledAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    isDemo: z.boolean(),
    billing: BillingDtoSchema.nullable().optional(),
    profile: OrganizationProfileSchema.nullable(),
    clientCountries: z.array(z.string()).nullable(),
    allowedPlans: z.array(z.nativeEnum(FLAT_RATE_PLANS)).nullable(),
    aipProgramSince: z.string().date().nullable().optional(),
    billingTransferDiscountCode: z.string().nullable().optional(),
    billingTransferMode: z
        .nativeEnum(OrganizationBillingTransferMode)
        .optional(),
    userAssignments: z.array(OrganizationUserAssignmentSchema).optional(),
});

export type Organization = z.infer<typeof OrganizationDtoSchema>;
export type OrganizationProfile = z.infer<typeof OrganizationProfileSchema>;
export type OrganizationUserAssignment = z.infer<
    typeof OrganizationUserAssignmentSchema
>;

export const OrganizationIdNameLogoUrlSchema = OrganizationDtoSchema.pick({
    id: true,
    name: true,
    logoUrl: true,
});

export type OrganizationIdNameLogoUrl = z.infer<
    typeof OrganizationIdNameLogoUrlSchema
>;

export const OrganizationIdNameLogoUrlArraySchema = z.array(
    OrganizationIdNameLogoUrlSchema,
);

export const OrganizationsListResponseSchema = z.object({
    organizations: OrganizationIdNameLogoUrlArraySchema,
});

export type OrganizationsListResponse = z.infer<
    typeof OrganizationsListResponseSchema
>;
